import { useRef, useState, useCallback, useEffect, useMemo, useContext } from 'react';
import { flushSync } from 'react-dom';
import { Button, Popover, Select, Spin, message } from 'antd';
import { DeleteOutlined, DownOutlined } from '@ant-design/icons';
import { PromptEditModal } from '../components';
import MaterialLibraryModal from '../../components/MaterialLibraryModal';
import { uploadFlowImage } from '../../execution';
import Utils from '@utils/utils';
import eventBus from '@utils/eventBus';
import { AppContext } from '@utils/AppContext';
import { RESOLUTION_RATIO } from '@utils/CONST';
import styles from '../CreativeImg2Img/index.module.scss';

const LARGE_ICON = 'https://photostudio-ai-oss.arcsoft.com.cn/photostudio-opshub/FreeCanvas/large.svg';
const FLOW_LINK_PLACEHOLDER_ICON = 'https://photostudio-ai-oss.arcsoft.com.cn/photostudio-opshub/FreeCanvas/node-image.svg';

const SLOT_COUNT = 14;
const MODEL_OPTIONS = [
    { value: 'Nano2', label: 'Nano2' },
    { value: 'NanoPro', label: 'NanoPro' },
];
const RATIO_OPTIONS = [
    { value: RESOLUTION_RATIO.SMART, label: '智能', previewClassName: 'ratioPreviewModel' },
    { value: RESOLUTION_RATIO.SQUARE, label: '1:1', previewClassName: 'ratioPreviewSquare' },
    { value: RESOLUTION_RATIO.THREE_FOUR, label: '3:4', previewClassName: 'ratioPreviewPortrait' },
    { value: RESOLUTION_RATIO.FOUR_THREE, label: '4:3', previewClassName: 'ratioPreviewLandscape' },
    { value: RESOLUTION_RATIO.NINE_SIXTEEN, label: '9:16', previewClassName: 'ratioPreviewTall' },
    { value: RESOLUTION_RATIO.SIXTEEN_NINE, label: '16:9', previewClassName: 'ratioPreviewWide' },
    { value: RESOLUTION_RATIO.TWO_THREE, label: '2:3', previewClassName: 'ratioPreviewTwoThree' },
    { value: RESOLUTION_RATIO.THREE_TWO, label: '3:2', previewClassName: 'ratioPreviewThreeTwo' },
];
const RESOLUTION_OPTIONS = [
    { value: '2K', label: '2K' },
    { value: '4K', label: '4K' },
];
const COUNT_OPTIONS = [
    { value: 1, label: '1张' },
    { value: 2, label: '2张' },
    { value: 3, label: '3张' },
    { value: 4, label: '4张' },
];
const DEFAULT_GENERATE_COUNT = 4;
const getMaterialItemUrl = (item) => item?.imageUrlWithVersion || item?.imageUrl || '';

const buildUploadItems = (manualUploads = [], linkedUploads = [], preferredOrder = []) => {
    const manualQueue = manualUploads
        .map((url, index) => ({ type: 'manual', url, manualIndex: index }))
        .filter((item) => !!item.url);
    const linkedQueue = linkedUploads
        .map((item, index) => ({
            type: 'linked',
            url: item?.url || '',
            linkedIndex: index,
            linkedItem: item,
        }))
        .filter((item) => !!item.url);
    const items = [];
    const consumeItem = (queue, url) => {
        const index = queue.findIndex((item) => item.url === url);
        if (index < 0) return false;
        items.push(queue.splice(index, 1)[0]);
        return true;
    };
    const consumeLinkedItem = (edgeId) => {
        const index = linkedQueue.findIndex((item) => String(item.linkedItem?.edgeId || '') === String(edgeId || ''));
        if (index < 0) return false;
        items.push(linkedQueue.splice(index, 1)[0]);
        return true;
    };
    const consumeManualItem = (url) => consumeItem(manualQueue, url);

    (Array.isArray(preferredOrder) ? preferredOrder : []).filter(Boolean).forEach((orderItem) => {
        if (items.length >= SLOT_COUNT) return;

        if (orderItem && typeof orderItem === 'object') {
            if (orderItem.type === 'linked' && consumeLinkedItem(orderItem.edgeId)) return;
            if (orderItem.type === 'manual' && consumeManualItem(orderItem.url)) return;
            if (consumeItem(manualQueue, orderItem.url)) return;
            consumeItem(linkedQueue, orderItem.url);
            return;
        }

        if (consumeItem(manualQueue, orderItem)) return;
        consumeItem(linkedQueue, orderItem);
    });

    manualQueue.concat(linkedQueue).forEach((item) => {
        if (items.length < SLOT_COUNT) {
            items.push(item);
        }
    });

    return items;
};

const extractManualUploads = (manualUploads = [], linkedUploads = []) => {
    if (Array.isArray(manualUploads) && manualUploads.length > 0) {
        return manualUploads.slice(0, SLOT_COUNT);
    }

    const linkedUrlCountMap = linkedUploads.reduce((acc, item) => {
        const url = item?.url || '';
        if (!url) return acc;
        acc.set(url, (acc.get(url) || 0) + 1);
        return acc;
    }, new Map());

    return (Array.isArray(manualUploads) ? manualUploads : [])
        .filter(Boolean)
        .filter((url) => {
            const remainCount = linkedUrlCountMap.get(url) || 0;
            if (remainCount <= 0) return true;
            linkedUrlCountMap.set(url, remainCount - 1);
            return false;
        })
        .slice(0, SLOT_COUNT);
};

const isSameValue = (left, right) => JSON.stringify(left) === JSON.stringify(right);
const isValidCount = (count) => COUNT_OPTIONS.some((option) => option.value === count);
const normalizeCount = (count) => (isValidCount(count) ? count : DEFAULT_GENERATE_COUNT);
const REFERENCE_UPLOAD_LIMIT = SLOT_COUNT - 1;
const countNonMaterialUploads = (uploads = [], materialSelections = []) => {
    const materialUrlCountMap = (Array.isArray(materialSelections) ? materialSelections : []).reduce((acc, item) => {
        const url = getMaterialItemUrl(item);
        if (!url) return acc;
        acc.set(url, (acc.get(url) || 0) + 1);
        return acc;
    }, new Map());

    return (Array.isArray(uploads) ? uploads : []).filter(Boolean).reduce((count, url) => {
        const remainCount = materialUrlCountMap.get(url) || 0;
        if (remainCount > 0) {
            materialUrlCountMap.set(url, remainCount - 1);
            return count;
        }
        return count + 1;
    }, 0);
};

const normalizeTemplateReplaceUploads = (uploads = []) => {
    const nextUploads = Array.isArray(uploads) ? uploads.slice(0, SLOT_COUNT) : [];
    const modelImage = nextUploads[0] || '';
    const trailingUploads = nextUploads.slice(1).filter(Boolean);
    const normalizedUploads = modelImage ? [modelImage, ...trailingUploads] : ['', ...trailingUploads];

    while (normalizedUploads.length > 1 && !normalizedUploads[normalizedUploads.length - 1]) {
        normalizedUploads.pop();
    }

    return normalizedUploads.slice(0, SLOT_COUNT);
};

const TemplateReplacePanel = ({
    nodeId,
    nodeData,
    onChange,
    disabled = false,
}) => {
    const { globalUser } = useContext(AppContext);
    const inputRef = useRef(null);
    const promptInputRef = useRef(null);
    const uploadIntentRef = useRef({ mode: 'append', targetIndex: null });
    const materialIntentRef = useRef({ mode: 'append', targetIndex: null });
    const panelState = nodeData?.panelState || {};
    const inputParams = nodeData?.inputParams || {};
    const linkedSlotSources = useMemo(() => (
        panelState.linkedSlotSources
        && typeof panelState.linkedSlotSources === 'object'
        && !Array.isArray(panelState.linkedSlotSources)
            ? panelState.linkedSlotSources
            : {}
    ), [panelState.linkedSlotSources]);
    const linkedUploads = useMemo(
        () => (Array.isArray(panelState.linkedUploads) ? panelState.linkedUploads : []),
        [panelState.linkedUploads],
    );
    const getCurrentManualUploads = useCallback(() => (
        extractManualUploads(
            Array.isArray(panelState.manualUploads)
                ? panelState.manualUploads
                : Array.isArray(panelState.uploads)
                    ? panelState.uploads
                    : Array.isArray(inputParams.imageUrls)
                        ? inputParams.imageUrls
                        : [inputParams.srcImage, inputParams.refImage].filter(Boolean),
            linkedUploads,
        )
    ), [inputParams.imageUrls, inputParams.refImage, inputParams.srcImage, linkedUploads, panelState.manualUploads, panelState.uploads]);
    const [uploads, setUploads] = useState(() => (
        getCurrentManualUploads()
    ));
    const [prompt, setPrompt] = useState(panelState.prompt ?? inputParams.prompt ?? '');
    const [model, setModel] = useState(
        panelState.model
        || inputParams.model
        || MODEL_OPTIONS[1].value,
    );
    const [ratio, setRatio] = useState(
        panelState.aspectRatio
        || inputParams.aspectRatio
        || RESOLUTION_RATIO.THREE_FOUR,
    );
    const [resolution, setResolution] = useState(
        panelState.imageSize
        || inputParams.imageSize
        || '2K',
    );
    const [generateCount, setGenerateCount] = useState(
        normalizeCount(panelState.num ?? nodeData?.inputParams?.num ?? DEFAULT_GENERATE_COUNT),
    );
    const [skipSelect, setSkipSelect] = useState(panelState.skipSelect ?? !!inputParams.skipSelect);
    const [editorOpen, setEditorOpen] = useState(false);
    const [configPanelOpen, setConfigPanelOpen] = useState(false);
    const [uploadingCount, setUploadingCount] = useState(0);
    const [materialModalOpen, setMaterialModalOpen] = useState(false);
    const [materialSelections, setMaterialSelections] = useState(() => (
        Array.isArray(panelState.materialSelections) ? panelState.materialSelections.filter(Boolean) : []
    ));
    const [pickerAllowsMultiple, setPickerAllowsMultiple] = useState(true);
    const getCurrentUploadOrder = useCallback(() => (
        Array.isArray(panelState.uploadItems)
            ? panelState.uploadItems
            : Array.isArray(panelState.uploads)
                ? panelState.uploads.filter(Boolean)
                : Array.isArray(inputParams.imageUrls)
                    ? inputParams.imageUrls.filter(Boolean)
                    : [inputParams.srcImage, inputParams.refImage].filter(Boolean)
    ), [inputParams.imageUrls, inputParams.refImage, inputParams.srcImage, panelState.uploadItems, panelState.uploads]);
    const [uploadOrder, setUploadOrder] = useState(() => getCurrentUploadOrder());
    const uploadItems = useMemo(() => (
        buildUploadItems(uploads, linkedUploads, uploadOrder)
    ), [linkedUploads, uploadOrder, uploads]);
    const visibleSlots = useMemo(() => {
        const sparseUploads = normalizeTemplateReplaceUploads(uploads);
        const slot0Url = linkedSlotSources.source?.url || sparseUploads[0] || '';
        const manualReferenceValues = sparseUploads.slice(1).filter(Boolean);
        const extraLinkedReferenceItems = Array.isArray(linkedUploads) ? linkedUploads.filter((item) => !!item?.url) : [];
        const primaryLinkedReferenceUrl = linkedSlotSources.reference?.url || '';
        const slot1Url = primaryLinkedReferenceUrl || extraLinkedReferenceItems[0]?.url || manualReferenceValues[0] || '';
        const slotItems = [
            linkedSlotSources.source?.edgeId
                ? { type: 'linked', url: slot0Url, linkedItem: linkedSlotSources.source, manualIndex: 0, slotIndex: 0, slotKey: 'source' }
                : { type: slot0Url ? 'manual' : 'empty', url: slot0Url, manualIndex: 0, slotIndex: 0, slotKey: 'source' },
            primaryLinkedReferenceUrl
                ? { type: 'linked', url: slot1Url, linkedItem: linkedSlotSources.reference, manualIndex: 1, slotIndex: 1, slotKey: 'reference' }
                : extraLinkedReferenceItems[0]
                    ? { type: 'linked', url: slot1Url, linkedItem: extraLinkedReferenceItems[0], manualIndex: 1, slotIndex: 1, slotKey: 'reference' }
                    : { type: slot1Url ? 'manual' : 'empty', url: slot1Url, manualIndex: 1, slotIndex: 1, slotKey: 'reference' },
            ...(
                primaryLinkedReferenceUrl
                    ? extraLinkedReferenceItems
                    : extraLinkedReferenceItems.slice(1)
            ).map((item, index) => ({
                type: 'linked',
                url: item.url,
                linkedItem: item,
                manualIndex: index + 2,
                slotIndex: index + 2,
                slotKey: 'reference',
            })),
            ...(primaryLinkedReferenceUrl || extraLinkedReferenceItems.length > 0
                ? manualReferenceValues
                : manualReferenceValues.slice(1)
            ).map((url, index) => ({
                type: 'manual',
                url,
                manualIndex: index + 1,
                slotIndex: index + 2 + extraLinkedReferenceItems.length - (primaryLinkedReferenceUrl ? 0 : 1),
            })),
        ];
        const highestFilledIndex = slotItems.reduce((maxIndex, item, index) => (
            item?.url ? index : maxIndex
        ), -1);
        const nextEmptyIndex = Math.min(
            Math.max(highestFilledIndex + 1, 1),
            SLOT_COUNT - 1,
        );
        const slotCount = Math.min(SLOT_COUNT, Math.max(2, nextEmptyIndex + 1));

        return Array.from({ length: slotCount }, (_, index) => (
            slotItems[index] || {
                type: 'empty',
                url: '',
                manualIndex: index,
                slotIndex: index,
            }
        ));
    }, [linkedSlotSources.reference, linkedSlotSources.source, linkedUploads, uploads]);
    const mergedUploads = useMemo(
        () => visibleSlots
            .map((item) => (item?.url ? {
                url: item.url,
                source: item.type === 'linked' ? 'line' : 'upload',
            } : null))
            .filter(Boolean),
        [visibleSlots],
    );
    const occupiedReferenceSlotCount = useMemo(
        () => visibleSlots.slice(1).filter((item) => !!item?.url).length,
        [visibleSlots],
    );
    const referenceOccupiedCount = useMemo(() => {
        const linkedReferenceCount = [
            linkedSlotSources.reference?.url || '',
            ...linkedUploads.map((item) => item?.url || ''),
        ].filter(Boolean).length;
        const manualReferenceCount = uploads.slice(1).filter(Boolean).length;
        return linkedReferenceCount + manualReferenceCount;
    }, [linkedSlotSources.reference?.url, linkedUploads, uploads]);
    const materialLibraryMaxSelectCount = useMemo(() => {
        if (materialIntentRef.current?.mode === 'model') {
            return 1;
        }

        const localUploadReferenceCount = countNonMaterialUploads(uploads.slice(1), materialSelections);
        const linkedReferenceCount = [
            linkedSlotSources.reference?.url || '',
            ...linkedUploads.map((item) => item?.url || ''),
        ].filter(Boolean).length;

        return Math.max(1, REFERENCE_UPLOAD_LIMIT - localUploadReferenceCount - linkedReferenceCount);
    }, [linkedSlotSources.reference?.url, linkedUploads, materialSelections, uploads]);
    const lastSubmittedRef = useRef('');
    const adjustPromptHeight = useCallback(() => {
        const textarea = promptInputRef.current;
        if (!textarea) return;

        const computedStyle = window.getComputedStyle(textarea);
        const lineHeight = parseFloat(computedStyle.lineHeight) || 22;
        const maxHeight = lineHeight * 6;
        const minHeight = lineHeight * 3;

        textarea.style.height = `${minHeight}px`;
        textarea.style.overflowY = 'hidden';

        const nextHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
        textarea.style.height = `${nextHeight}px`;
        textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }, []);

    useEffect(() => {
        const nextUploads = getCurrentManualUploads();
        const nextUploadOrder = getCurrentUploadOrder();
        const nextPrompt = panelState.prompt ?? inputParams.prompt ?? '';
        const nextModel = (
            panelState.model
            || inputParams.model
            || MODEL_OPTIONS[1].value
        );
        const nextRatio = (
            panelState.aspectRatio
            || inputParams.aspectRatio
            || RESOLUTION_RATIO.THREE_FOUR
        );
        const nextResolution = (
            panelState.imageSize
            || inputParams.imageSize
            || '2K'
        );
        const nextGenerateCount = normalizeCount(panelState.num ?? nodeData?.inputParams?.num ?? DEFAULT_GENERATE_COUNT);
        const nextSkipSelect = panelState.skipSelect ?? !!inputParams.skipSelect;
        const nextMaterialSelections = Array.isArray(panelState.materialSelections) ? panelState.materialSelections.filter(Boolean) : [];

        setUploads((prev) => (isSameValue(prev, nextUploads) ? prev : nextUploads));
        setUploadOrder((prev) => (isSameValue(prev, nextUploadOrder) ? prev : nextUploadOrder));
        setPrompt((prev) => (prev === nextPrompt ? prev : nextPrompt));
        setModel((prev) => (prev === nextModel ? prev : nextModel));
        setRatio((prev) => (prev === nextRatio ? prev : nextRatio));
        setResolution((prev) => (prev === nextResolution ? prev : nextResolution));
        setGenerateCount((prev) => (prev === nextGenerateCount ? prev : nextGenerateCount));
        setSkipSelect((prev) => (prev === nextSkipSelect ? prev : nextSkipSelect));
        setMaterialSelections((prev) => (isSameValue(prev, nextMaterialSelections) ? prev : nextMaterialSelections));
    }, [
        inputParams.aspectRatio,
        inputParams.imageSize,
        inputParams.imageUrls,
        inputParams.model,
        inputParams.prompt,
        inputParams.refImage,
        inputParams.skipSelect,
        inputParams.srcImage,
        nodeData?.inputParams?.num,
        panelState.aspectRatio,
        panelState.imageSize,
        panelState.manualUploads,
        panelState.materialSelections,
        panelState.model,
        panelState.num,
        panelState.prompt,
        panelState.skipSelect,
        panelState.uploadItems,
        panelState.uploads,
        getCurrentUploadOrder,
        getCurrentManualUploads,
    ]);

    useEffect(() => {
        const currentUrls = new Set(uploads.filter(Boolean));
        setMaterialSelections((prev) => prev.filter((item) => currentUrls.has(getMaterialItemUrl(item))));
    }, [uploads]);

    useEffect(() => {
        adjustPromptHeight();
    }, [adjustPromptHeight, prompt]);

    const replaceModelImage = useCallback((imageUrl) => {
        if (!imageUrl) return;
        setUploads((prev) => normalizeTemplateReplaceUploads([imageUrl, ...prev.slice(1)]));
        setUploadOrder((prev) => {
            const nextOrder = prev.slice();
            const modelItem = { type: 'manual', url: imageUrl };
            if (nextOrder.length > 0) {
                nextOrder.splice(0, 1, modelItem);
            } else {
                nextOrder.unshift(modelItem);
            }
            return nextOrder.slice(0, SLOT_COUNT);
        });
    }, []);

    const appendUploads = useCallback((imageUrls = []) => {
        const validImageUrls = imageUrls.filter(Boolean);
        if (validImageUrls.length === 0) return;

        setUploads((prev) => normalizeTemplateReplaceUploads(prev.concat(validImageUrls)));
        setUploadOrder((prev) => (
            prev
                .concat(validImageUrls.map((url) => ({ type: 'manual', url })))
                .slice(0, SLOT_COUNT)
        ));
    }, []);

    const fillUploadsAt = useCallback((imageUrls = [], targetIndex = null) => {
        const validImageUrls = imageUrls.filter(Boolean);
        if (validImageUrls.length === 0) return;

        setUploads((prev) => {
            const nextUploads = prev.slice(0, SLOT_COUNT);
            const normalizedTargetIndex = Math.min(
                Math.max(Number.isFinite(targetIndex) ? targetIndex : nextUploads.length, 0),
                SLOT_COUNT - 1,
            );
            validImageUrls.forEach((url, index) => {
                const slotIndex = normalizedTargetIndex + index;
                if (slotIndex < SLOT_COUNT) {
                    nextUploads[slotIndex] = url;
                }
            });
            return normalizeTemplateReplaceUploads(nextUploads);
        });
        setUploadOrder((prev) => {
            const nextOrder = prev.slice();
            const normalizedTargetIndex = Math.min(
                Math.max(Number.isFinite(targetIndex) ? targetIndex : nextOrder.length, 0),
                SLOT_COUNT - 1,
            );
            nextOrder.splice(normalizedTargetIndex, validImageUrls.length, ...validImageUrls.map((url) => ({ type: 'manual', url })));
            return nextOrder.slice(0, SLOT_COUNT);
        });
    }, []);

    const handleSelectMaterial = useCallback((imageUrlOrUrls, selectedItems = []) => {
        if (!imageUrlOrUrls || disabled) return;
        const nextImageUrls = Array.isArray(imageUrlOrUrls) ? imageUrlOrUrls.filter(Boolean) : [imageUrlOrUrls].filter(Boolean);
        if (nextImageUrls.length === 0) return;
        if (materialIntentRef.current?.mode === 'model') {
            replaceModelImage(nextImageUrls[0]);
            return;
        }
        const nextSelectedItems = Array.isArray(selectedItems) ? selectedItems.filter(Boolean) : [];
        const previousMaterialUrlSet = new Set(materialSelections.map((item) => getMaterialItemUrl(item)).filter(Boolean));
        const nextMaterialUrlSet = new Set(nextSelectedItems.map((item) => getMaterialItemUrl(item)).filter(Boolean));
        const appendUrls = nextImageUrls.filter((url) => !previousMaterialUrlSet.has(url));
        const removedUrls = [...previousMaterialUrlSet].filter((url) => !nextMaterialUrlSet.has(url));
        setMaterialSelections(nextSelectedItems);
        if (removedUrls.length > 0) {
            setUploads((prev) => normalizeTemplateReplaceUploads(
                prev.map((url, index) => (
                    index === 0 || !removedUrls.includes(url) ? url : ''
                )),
            ));
            setUploadOrder((prev) => prev.filter((item) => !removedUrls.includes(item?.url)));
        }
        if (appendUrls.length === 0) return;
        if (materialIntentRef.current?.mode === 'slot') {
            fillUploadsAt(appendUrls, materialIntentRef.current?.targetIndex);
            return;
        }
        appendUploads(appendUrls);
    }, [appendUploads, disabled, fillUploadsAt, materialSelections, replaceModelImage]);

    const handlePickImage = useCallback((event, intent = { mode: 'append', targetIndex: null }) => {
        if (disabled) return;
        event?.preventDefault?.();
        event?.stopPropagation?.();

        const normalizedIntent = typeof intent === 'string'
            ? { mode: intent, targetIndex: null }
            : { mode: intent?.mode || 'append', targetIndex: intent?.targetIndex ?? null };
        uploadIntentRef.current = normalizedIntent;
        setPickerAllowsMultiple(normalizedIntent.mode !== 'model');
        const inputElement = inputRef.current;
        if (!inputElement) return;

        if (typeof inputElement.showPicker === 'function') {
            try {
                inputElement.showPicker();
                return;
            } catch (error) {
                // Fall back to click() when showPicker is unavailable or blocked.
            }
        }

        inputElement.click();
    }, [disabled]);

    const handleFileChange = useCallback(async (event) => {
        if (disabled) {
            event.target.value = '';
            return;
        }
        const fileList = Array.from(event.target.files || []);
        const uploadIntent = uploadIntentRef.current || { mode: 'append', targetIndex: null };
        event.target.value = '';
        if (fileList.length === 0) return;

        if (uploadIntent.mode === 'model') {
            const modelFile = fileList[0];
            if (!modelFile) return;

            flushSync(() => { setUploadingCount((prev) => prev + 1); });
            let modelUrl = '';
            try {
                modelUrl = await uploadFlowImage(modelFile);
            } catch (error) {
                modelUrl = '';
            }
            setUploadingCount((prev) => Math.max(0, prev - 1));
            if (modelUrl) {
                replaceModelImage(modelUrl);
            }
            return;
        }

        const remainCount = Math.max(0, REFERENCE_UPLOAD_LIMIT - referenceOccupiedCount);
        if (remainCount === 0) {
            message.warning(`最多上传${REFERENCE_UPLOAD_LIMIT}张参考图`);
            return;
        }

        if (fileList.length > remainCount) {
            message.warning(`最多上传${REFERENCE_UPLOAD_LIMIT}张参考图`);
        }

        const nextFiles = fileList.slice(0, remainCount);
        flushSync(() => { setUploadingCount((prev) => prev + nextFiles.length); });
        const uploadedUrls = await Promise.all(nextFiles.map(async (file) => {
            try {
                return await uploadFlowImage(file);
            } catch (error) {
                return '';
            }
        }));
        setUploadingCount((prev) => Math.max(0, prev - nextFiles.length));
        if (uploadIntent.mode === 'slot') {
            fillUploadsAt(uploadedUrls, uploadIntent.targetIndex);
            return;
        }
        appendUploads(uploadedUrls);
    }, [appendUploads, disabled, fillUploadsAt, referenceOccupiedCount, replaceModelImage]);

    const handleUploadSlotDragOver = useCallback((event) => {
        const hasFile = Array.from(event.dataTransfer?.types || []).includes('Files');
        if (!hasFile) return;

        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = 'copy';
    }, []);

    const handleUploadSlotDrop = useCallback(async (event, targetIndex = mergedUploads.length) => {
        if (disabled) return;

        const fileList = Array.from(event.dataTransfer?.files || []);
        const imageFiles = fileList.filter((file) => file?.type?.startsWith('image/'));
        if (imageFiles.length === 0) return;

        event.preventDefault();
        event.stopPropagation();

        const normalizedTargetIndex = Math.min(
            Math.max(Number.isFinite(targetIndex) ? targetIndex : mergedUploads.length, 0),
            mergedUploads.length,
        );
        const targetItem = visibleSlots[normalizedTargetIndex];
        if (targetItem?.type === 'linked') {
            message.warning('请先解除连线再上传图片');
            return;
        }
        const isReplacingExistingSlot = normalizedTargetIndex < mergedUploads.length;
        const availableCount = Math.max(0, REFERENCE_UPLOAD_LIMIT - referenceOccupiedCount + (isReplacingExistingSlot ? 1 : 0));
        if (availableCount === 0) {
            message.warning(`最多上传${REFERENCE_UPLOAD_LIMIT}张参考图`);
            return;
        }

        if (imageFiles.length > availableCount) {
            message.warning(`最多上传${REFERENCE_UPLOAD_LIMIT}张参考图`);
        }

        const nextFiles = imageFiles.slice(0, availableCount);
        setUploadingCount((prev) => prev + nextFiles.length);
        await new Promise((resolve) => { setTimeout(resolve, 0); });
        const uploadedUrls = await Promise.all(nextFiles.map(async (file) => {
            try {
                return await uploadFlowImage(file);
            } catch (error) {
                return '';
            }
        }));
        setUploadingCount((prev) => Math.max(0, prev - nextFiles.length));
        const validUploadedUrls = uploadedUrls.filter(Boolean);
        if (validUploadedUrls.length === 0) return;

        setUploads((prev) => {
            const nextUploads = prev.slice(0, SLOT_COUNT);
            const nextPrimaryUrl = validUploadedUrls[0];
            const extraUrls = validUploadedUrls.slice(1);

            if (targetItem?.type === 'manual') {
                nextUploads[targetItem.manualIndex] = nextPrimaryUrl;
                extraUrls.forEach((url, index) => {
                    const slotIndex = targetItem.manualIndex + index + 1;
                    if (slotIndex < SLOT_COUNT) {
                        nextUploads[slotIndex] = url;
                    }
                });
                return normalizeTemplateReplaceUploads(nextUploads);
            }

            validUploadedUrls.forEach((url, index) => {
                const slotIndex = normalizedTargetIndex + index;
                if (slotIndex < SLOT_COUNT) {
                    nextUploads[slotIndex] = url;
                }
            });
            return normalizeTemplateReplaceUploads(nextUploads);
        });
        setUploadOrder((prev) => {
            const nextOrder = prev.slice();
            const newItems = validUploadedUrls.map((url) => ({ type: 'manual', url }));

            if (normalizedTargetIndex < nextOrder.length) {
                nextOrder.splice(normalizedTargetIndex, 1, ...newItems);
                return nextOrder.slice(0, SLOT_COUNT);
            }

            return nextOrder.concat(newItems).slice(0, SLOT_COUNT);
        });
    }, [disabled, nodeData, nodeId, referenceOccupiedCount, visibleSlots]);

    const handleRemoveImage = useCallback((index, event) => {
        if (disabled) return;
        event.stopPropagation();
        const item = visibleSlots[index];
        if (item?.type !== 'manual') return;
        setUploads((prev) => {
            const nextUploads = prev.slice(0, SLOT_COUNT);
            if (item.manualIndex === 0) {
                nextUploads[0] = '';
                return normalizeTemplateReplaceUploads(nextUploads);
            }

            nextUploads.splice(item.manualIndex, 1);
            return normalizeTemplateReplaceUploads(nextUploads);
        });
        setUploadOrder((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
    }, [disabled, visibleSlots]);

    const handleRemoveLinkedImage = useCallback((index, event) => {
        if (disabled) return;
        event.stopPropagation();
        const targetItem = visibleSlots[index];
        if (targetItem?.type !== 'linked' || !targetItem?.linkedItem?.edgeId || !targetItem?.slotKey) return;
        nodeData?.onRemoveLinkedInput?.(nodeId, targetItem.slotKey, targetItem.linkedItem);
    }, [disabled, nodeData, nodeId, visibleSlots]);

    const handleGenerate = useCallback(({ count }) => {
        if (disabled) return;
        const modelImage = linkedSlotSources.source?.url || uploads[0] || '';
        const manualReferenceValues = uploads.slice(1).filter(Boolean);
        const referenceImages = [
            linkedSlotSources.reference?.url || '',
            ...linkedUploads.map((item) => item?.url || '').filter(Boolean),
            ...(linkedSlotSources.reference?.url ? manualReferenceValues : manualReferenceValues.slice(1)),
            !linkedSlotSources.reference?.url ? (manualReferenceValues[0] || '') : '',
        ].filter(Boolean);
        if (!modelImage) {
            message.warning('请先上传模特图');
            return;
        }
        if (referenceImages.length === 0) {
            message.warning('请先上传后面的图');
            return;
        }
        if (!prompt.trim()) {
            message.warning('请先输入提示词');
            return;
        }
        if (Number(globalUser?.membership?.level) === 0) {
            message.warning('该功能为会员用户专属,请充值会员后使用');
            eventBus.emit('recharge');
            return;
        }

        nodeData?.onExecute?.(nodeId, { count });
        nodeData?.onGenerateComplete?.(nodeId);
    }, [disabled, globalUser?.membership?.level, linkedSlotSources.reference?.url, linkedSlotSources.source?.url, linkedUploads, nodeData, nodeId, prompt, uploads]);

    useEffect(() => {
        if (!nodeId || typeof onChange !== 'function') return;
        if (nodeData?.functionKey !== 'templateReplace') return;

        const persistedManualUploads = [
            linkedSlotSources.source?.url ? '' : (uploads[0] || ''),
            ...uploads.slice(1).filter(Boolean),
        ].slice(0, SLOT_COUNT);

        const nextPatch = {
            inputParams: {
                imageUrls: mergedUploads,
                srcImage: linkedSlotSources.source?.url || uploads[0] || '',
                refImage: linkedSlotSources.reference?.url || uploads[1] || '',
                prompt,
                model,
                aspectRatio: ratio,
                imageSize: resolution,
                num: normalizeCount(generateCount),
                skipSelect: 0,
            },
            panelState: {
                manualUploads: persistedManualUploads,
                linkedUploads,
                uploadItems: visibleSlots
                    .filter((item) => !!item?.url)
                    .map((item) => (
                    item.type === 'linked'
                        ? {
                            type: 'linked',
                            edgeId: item.linkedItem?.edgeId || '',
                            url: item.url,
                        }
                        : {
                            type: 'manual',
                            url: item.url,
                        }
                    )),
                uploads: mergedUploads,
                linkedSlotSources,
                prompt,
                model,
                aspectRatio: ratio,
                imageSize: resolution,
                num: normalizeCount(generateCount),
                skipSelect,
                materialSelections,
            },
        };
        const nextSignature = JSON.stringify(nextPatch);

        if (
            lastSubmittedRef.current === nextSignature
            || (
                isSameValue(nodeData?.inputParams || {}, nextPatch.inputParams)
                && isSameValue(nodeData?.panelState || {}, nextPatch.panelState)
            )
        ) {
            lastSubmittedRef.current = nextSignature;
            return;
        }

        lastSubmittedRef.current = nextSignature;
        onChange(nodeId, nextPatch);
    }, [generateCount, linkedSlotSources, linkedUploads, materialSelections, mergedUploads, model, nodeData?.inputParams, nodeData?.panelState, nodeId, onChange, prompt, ratio, resolution, skipSelect, uploads, visibleSlots]);

    const selectedResolutionLabel = RESOLUTION_OPTIONS.find((item) => item.value === resolution)?.label || RESOLUTION_OPTIONS[0].label;
    const selectedRatioOption = RATIO_OPTIONS.find((item) => item.value === ratio) || RATIO_OPTIONS[0];
    const selectedRatioLabel = selectedRatioOption?.label || RATIO_OPTIONS[0].label;
    const configTriggerLabel = `${selectedRatioLabel} • ${selectedResolutionLabel}`;
    const currentExecutionStatus = String(
        nodeData?.latestExecution?.status
        || nodeData?.executionStatus
        || 'idle',
    ).toLowerCase();
    const isGenerating = currentExecutionStatus === 'processing';
    const canGenerate = !disabled
        && !isGenerating
        && !!(linkedSlotSources.source?.url || uploads[0])
        && !!(linkedSlotSources.reference?.url || linkedUploads.some((item) => !!item?.url) || uploads.slice(1).some(Boolean))
        && prompt.trim().length > 0;
    const totalCost = (resolution === '4K' ? 120 : 80) * normalizeCount(generateCount);
    const configPanelContent = (
        <div
            className={styles.configPanel}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
        >
            <div className={styles.configSection}>
                <p className={styles.configTitle}>分辨率</p>
                <div className={styles.resolutionOptionGrid}>
                    {RESOLUTION_OPTIONS.map((item) => (
                        <button
                            key={item.value}
                            type="button"
                            className={[
                                styles.optionButton,
                                styles.resolutionOptionButton,
                                resolution === item.value ? styles.optionButtonActive : '',
                            ].filter(Boolean).join(' ')}
                            onMouseDown={(event) => event.stopPropagation()}
                            onClick={(event) => {
                                event.stopPropagation();
                                setResolution(item.value);
                            }}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className={styles.configSection}>
                <p className={styles.configTitle}>比例</p>
                <div className={styles.ratioOptionGrid}>
                    {RATIO_OPTIONS.map((item) => (
                        <button
                            key={item.value}
                            type="button"
                            className={[
                                styles.optionButton,
                                styles.ratioOptionButton,
                                ratio === item.value ? styles.optionButtonActive : '',
                            ].filter(Boolean).join(' ')}
                            onMouseDown={(event) => event.stopPropagation()}
                            onClick={(event) => {
                                event.stopPropagation();
                                setRatio(item.value);
                            }}
                        >
                            <span className={[styles.ratioPreview, styles[item.previewClassName]].join(' ')}></span>
                            <span>{item.label}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
    const generateButton = (
        <Button
            type="primary"
            className={styles.generateBtn}
            disabled={!canGenerate}
            onClick={canGenerate ? () => handleGenerate({ count: normalizeCount(generateCount) }) : undefined}
        >
            <span className={styles.generateBtnContent}>
                <span>{isGenerating ? '生成中' : '生成'}</span>
                {!isGenerating ? (
                    <span className={styles.generateBtnCost}>
                        <img src={require('@assets/images/arcbean3.png')} alt="" />
                        <span>{totalCost}</span>
                    </span>
                ) : null}
                {!isGenerating ? <span className={styles.teamIcon}></span> : null}
            </span>
        </Button>
    );

    return (
        <div className={`${styles.panel} ${disabled ? styles.panelDisabled : ''}`}>
            <div className={styles.content}>
                <div className={styles.uploadGrid}>
                    {visibleSlots.map((item, index) => {
                        const image = item.url;
                        const isFilled = !!image;
                        const isLinkedPlaceholder = image === FLOW_LINK_PLACEHOLDER_ICON;
                        const displayImage = image
                            ? (isLinkedPlaceholder ? image : Utils.getImgThumbnail(image))
                            : '';
                        const isModelSlot = index === 0;
                        const slotLabel = isModelSlot ? '模特图' : (index === 1 ? '参考图' : null);

                        return (
                            <button
                                key={`template-replace-slot-${index}`}
                                type="button"
                                className={`${styles.uploadSlot} ${isFilled ? styles.uploadSlotFilled : styles.uploadSlotEmpty}`}
                                onDragOver={handleUploadSlotDragOver}
                                onDrop={(event) => handleUploadSlotDrop(event, index)}
                                onClick={(event) => {
                                    if (disabled) {
                                        event.stopPropagation();
                                        return;
                                    }
                                    if (isFilled) {
                                        event.preventDefault();
                                        event.stopPropagation();
                                    }
                                }}
                                aria-label={isFilled ? `删除输入图 ${index + 1}` : `上传输入图 ${index + 1}`}
                            >
                                {isFilled ? (
                                    isLinkedPlaceholder ? (
                                        <span className={styles.linkedPlaceholder}>
                                            <img
                                                src={FLOW_LINK_PLACEHOLDER_ICON}
                                                alt=""
                                                className={styles.linkedPlaceholderIcon}
                                                draggable={false}
                                            />
                                            <span className={styles.linkedPlaceholderLabel}>
                                                {item?.linkedItem?.imageSequenceLabel || slotLabel || `图${index + 1}`}
                                            </span>
                                        </span>
                                    ) : (
                                        <img
                                            src={displayImage}
                                            alt={`输入图 ${index + 1}`}
                                            className={styles.uploadPreview}
                                            draggable={false}
                                        />
                                    )
                                ) : (
                                    <>
                                        <span className={styles.plusIcon} />
                                        {slotLabel ? <span className={styles.slotHint}>{slotLabel}</span> : null}
                                        <span className={styles.uploadSlotActions}>
                                            <Button
                                                size="small"
                                                className={styles.uploadSlotActionBtn}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handlePickImage(e, {
                                                        mode: isModelSlot ? 'model' : 'slot',
                                                        targetIndex: index,
                                                    });
                                                }}
                                            >
                                                本地上传
                                            </Button>
                                            {!isModelSlot ? (
                                                <Button
                                                    size="small"
                                                    className={styles.uploadSlotActionBtn}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        materialIntentRef.current = {
                                                            mode: 'slot',
                                                            targetIndex: index,
                                                        };
                                                        setMaterialModalOpen(true);
                                                    }}
                                                >
                                                    素材库
                                                </Button>
                                            ) : null}
                                        </span>
                                    </>
                                )}
                                {isModelSlot && isFilled ? <span className={styles.slotHintFilled}>模特图</span> : null}
                                {isFilled ? (
                                    <span
                                        className={styles.removeMask}
                                        onMouseDown={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                        }}
                                        onClick={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            if (item.type === 'linked') {
                                                handleRemoveLinkedImage(index, event);
                                                return;
                                            }
                                            handleRemoveImage(index, event);
                                        }}
                                    >
                                        <span className={styles.removeButton}>
                                            <DeleteOutlined />
                                        </span>
                                    </span>
                                ) : null}
                            </button>
                        );
                    })}
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/*"
                        multiple={pickerAllowsMultiple}
                        className={styles.fileInput}
                        onChange={handleFileChange}
                    />
                </div>

                <div className={styles.promptBox}>
                    <textarea
                        ref={promptInputRef}
                        value={prompt}
                        onChange={(event) => setPrompt(event.target.value)}
                        disabled={disabled}
                        className={styles.promptInput}
                        placeholder="请描述您想要生成的效果，例如：将模特图的衣领改成参考图中的方领，保留原衣服颜色和面料不变。"
                        maxLength={1000}
                    />
                    <img
                        src={LARGE_ICON}
                        alt=""
                        width={16}
                        height={16}
                        className={styles.promptIcon}
                        draggable={false}
                        onClick={() => {
                            if (disabled) return;
                            setEditorOpen(true);
                        }}
                    />
                </div>
                <div className={styles.uploadParams}>
                    <div className={styles.leftGroups}>
                        <span className={styles.fixedModelTag}>全能模型</span>
                    </div>
                    <div className={styles.rightGroups}>
                        <Popover
                            trigger="click"
                            placement="topRight"
                            overlayClassName={styles.configPopover}
                            content={configPanelContent}
                            open={configPanelOpen}
                            onOpenChange={setConfigPanelOpen}
                        >
                            <button
                                type="button"
                                className={`${styles.configTrigger} ${configPanelOpen ? styles.configTriggerActive : ''}`}
                                onMouseDown={(event) => event.stopPropagation()}
                            >
                                <span className={styles.configTriggerText}>{configTriggerLabel}</span>
                                <DownOutlined className={`${styles.configTriggerArrow} ${configPanelOpen ? styles.configTriggerArrowOpen : ''}`} />
                            </button>
                        </Popover>
                        <Select
                            value={normalizeCount(generateCount)}
                            className={styles.countSelect}
                            onChange={(value) => setGenerateCount(normalizeCount(value))}
                            options={COUNT_OPTIONS}
                            disabled={disabled}
                            popupMatchSelectWidth={82}
                            getPopupContainer={(triggerNode) => triggerNode?.parentElement || triggerNode}
                        />
                    </div>
                    {generateButton}
                </div>
            </div>
            <PromptEditModal
                open={editorOpen}
                value={prompt}
                maxLength={1000}
                onCancel={() => setEditorOpen(false)}
                onSave={(value) => {
                    if (disabled) return;
                    setPrompt(value);
                    setEditorOpen(false);
                }}
            />
            <MaterialLibraryModal
                open={materialModalOpen}
                onClose={() => setMaterialModalOpen(false)}
                onSelect={handleSelectMaterial}
                multiple
                maxSelectCount={materialLibraryMaxSelectCount}
                initialSelectedItems={materialIntentRef.current?.mode !== 'model' ? materialSelections : []}
                showApplyAction={false}
            />
        </div>
    );
};

export default TemplateReplacePanel;
